import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../components/ModalProvider';
import { useToast } from '../components/Toast';
import { pageCustomers, customerDetail } from '../api/customers';
import { scopeUsers } from '../api/users';
import { historyOfCustomer, addCustomerNote } from '../api/history';
import { createCustomerTask } from '../api/tasks';
import ResultStatusesField from '../components/ResultStatusesField';
import { fmtDate, fmtDateTime, fmtVND, roleName } from '../utils/format';
import { RatioBadge } from '../components/Chips';
import CustomerRevenueChartModal from '../components/CustomerRevenueChartModal';

// Badge PHÂN CẤP KH theo thiết kế: VIP tím · Thân thiết xanh dương · Tiềm năng hổ phách · Phổ thông xám.
function LevelBadge({ level }) {
  if (!level) return <span className="small muted">—</span>;
  const s = level.toLowerCase();
  const style = s.includes('vip')
    ? { color: 'var(--vip)', background: 'color-mix(in srgb, var(--vip) 13%, transparent)' }
    : s.includes('thân thiết')
      ? { color: 'var(--info)', background: 'color-mix(in srgb, var(--info) 13%, transparent)' }
      : s.includes('tiềm năng')
        ? { color: 'var(--warn)', background: 'color-mix(in srgb, var(--warn-fill) 16%, transparent)' }
        : { color: 'var(--text3)', background: 'var(--panel2)' };
  return (
    <span style={{ ...style, display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 6, whiteSpace: 'nowrap' }}>
      {level}
    </span>
  );
}

export default function CustomersPage() {
  const { me } = useAuth();
  const { openModal, closeModal } = useModal();
  const toast = useToast();
  const [chart, setChart] = useState(null); // { code, name } khi mở biểu đồ doanh thu
  // Tạo công việc từ KH đã tích (GĐ/TP). picked = Set custId.
  const [picked, setPicked] = useState(() => new Set());
  const [taskOpen, setTaskOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', detail: '', deadline: '' });
  const [taskUseStatuses, setTaskUseStatuses] = useState(false);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [taskBusy, setTaskBusy] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [staff, setStaff] = useState([]);
  const [userId, setUserId] = useState('all');
  const [revTab, setRevTab] = useState('thisYear'); // 'thisYear' = có DT năm nay; 'stopped' = KH dừng; 'none' = chưa
  const [countThisYear, setCountThisYear] = useState(0);
  const [countStopped, setCountStopped] = useState(0);
  const [countNone, setCountNone] = useState(0);

  useEffect(() => {
    scopeUsers().then(setStaff).catch(() => setStaff([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    pageCustomers({ page, limit: pageSize, search, userId: userId === 'all' ? undefined : userId, revenue: revTab })
      .then((res) => {
        setList(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
        if (res.countThisYear != null) setCountThisYear(res.countThisYear);
        if (res.countStopped != null) setCountStopped(res.countStopped);
        if (res.countNone != null) setCountNone(res.countNone);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, search, userId, revTab]);

  function doSearch() {
    setPage(1);
    setSearch(searchInput.trim());
  }

  async function openHistory(c) {
    const detail = await customerDetail(c._id).catch(() => null);
    openModal(
      <>
        <span className="close" onClick={closeModal}>
          ×
        </span>
        <h3>
          {c.code} · {c.name}
        </h3>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          <span className="small">
            Sale: <b>{c.sale}</b>
          </span>
          {c.accountNo ? (
            <span className="small">
              Số TK: <b>{c.accountNo}</b>
            </span>
          ) : null}
          {c.level ? (
            <span className="small">
              Cấp KH: <b>{c.level}</b>
            </span>
          ) : null}
          {c.sourceTk ? (
            <span className="small">
              Nguồn TK: <b>{c.sourceTk}</b>
            </span>
          ) : null}
          {c.sourceType ? (
            <span className="small">
              Nguồn KH: <b>{c.sourceType}</b>
            </span>
          ) : null}
          {c.bank ? (
            <span className="small">
              TK NH: <b>{c.bank}</b>
            </span>
          ) : null}
          {c.createdDate ? (
            <span className="small">
              Ngày tạo: <b>{fmtDate(c.createdDate)}</b>
            </span>
          ) : null}
          <span className="small">
            Tổng tiền: <b>{fmtVND(c.total)}</b>
          </span>
          <span className="small">
            Tỷ lệ cọc: <b>{c.ratio}%</b>
          </span>
          <span className="small">
            Công nợ: <b>{fmtVND(c.debt)}</b>
          </span>
        </div>

        <SocsonDetail detail={detail} />

        <InternalExchange cust={c} />
        <div className="row" style={{ marginTop: 8 }}>
          <div className="spacer"></div>
          <button className="btn ghost" onClick={closeModal}>
            Đóng
          </button>
        </div>
      </>
    );
  }

  // GĐ/CV + Phòng CSKH (chăm sóc toàn bộ KH) xem toàn bộ; TP xem nhóm; NV xem của mình.
  const fullCust = me.role === 'GD' || me.role === 'CV' || (me.departments || []).includes('Phòng CSKH');
  const sub = fullCust ? 'Toàn bộ' : me.role === 'TP' ? 'Nhóm của bạn' : 'Của bạn';
  const tabWord = revTab === 'thisYear' ? 'phát sinh doanh thu' : revTab === 'stopped' ? 'đã dừng' : 'chưa phát sinh';

  // GĐ/CV/TP + Phòng CSKH (chăm sóc toàn bộ KH) tích chọn KH để tạo công việc đẩy về nhóm sale (PKD).
  const canTask = me.role === 'GD' || me.role === 'CV' || me.role === 'TP' || (me.departments || []).includes('Phòng CSKH');
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allShownPicked = list.length > 0 && list.every((c) => picked.has(c._id));
  const toggleAll = () => setPicked((s) => { const n = new Set(s); allShownPicked ? list.forEach((c) => n.delete(c._id)) : list.forEach((c) => n.add(c._id)); return n; });
  async function submitTask() {
    if (!taskForm.title.trim()) { toast('Nhập tiêu đề công việc', 'danger'); return; }
    if (!taskForm.deadline) { toast('Chọn hạn hoàn thành', 'danger'); return; }
    setTaskBusy(true);
    try {
      const r = await createCustomerTask({ title: taskForm.title.trim(), detail: taskForm.detail.trim(), deadline: taskForm.deadline, custIds: [...picked], statuses: taskUseStatuses ? taskStatuses : [] });
      toast(`Đã tạo ${r.tasksCreated} công việc${r.skipped?.length ? ` · bỏ qua ${r.skipped.length} KH` : ''}`);
      setTaskOpen(false); setPicked(new Set()); setTaskForm({ title: '', detail: '', deadline: '' }); setTaskUseStatuses(false); setTaskStatuses([]);
    } catch (e) { toast(e.response?.data?.message || 'Tạo công việc thất bại', 'danger'); }
    finally { setTaskBusy(false); }
  }

  return (
    <>
      <div className="page-head" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Quản lý khách hàng</h2>
          <div className="sub">Danh bạ khách hàng · {total.toLocaleString('vi-VN')} KH {tabWord} · {sub}</div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            style={{ width: 'auto', minWidth: 230 }}
            placeholder="🔍 Tìm mã KH, tên, sale…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <button className="btn sm" onClick={doSearch}>Tìm</button>
          {search && (
            <button className="btn ghost sm" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>✕</button>
          )}
          {staff.length > 0 && (
            <select style={{ width: 'auto', minWidth: 170 }} value={userId} onChange={(e) => { setPage(1); setUserId(e.target.value); }}>
              <option value="all">Tất cả nhân viên</option>
              {staff.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}{u.employeeCode ? ` (${u.employeeCode})` : ''}
                </option>
              ))}
            </select>
          )}
          {canTask && picked.size > 0 && (
            <button className="btn sm" onClick={() => setTaskOpen(true)}>📋 Tạo công việc ({picked.size})</button>
          )}
        </div>
      </div>

      {/* Tab lọc theo trạng thái doanh thu (chip theo thiết kế) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {[
          { k: 'thisYear', label: 'Phát sinh DT', n: countThisYear },
          { k: 'stopped', label: 'KH dừng', n: countStopped },
          { k: 'none', label: 'Chưa phát sinh', n: countNone },
        ].map((t) => {
          const on = revTab === t.k;
          return (
            <span
              key={t.k}
              onClick={() => { setRevTab(t.k); setPage(1); }}
              style={{ fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: '7px 13px', borderRadius: 8, color: on ? '#fff' : 'var(--text3)', background: on ? 'var(--accent)' : 'var(--panel2)' }}
            >
              {t.label} ({(t.n || 0).toLocaleString('vi-VN')})
            </span>
          );
        })}
      </div>
      <div className="card">
        <div className="row between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
          <span className="small muted">🔒 Không hiển thị SĐT / địa chỉ KH để tránh rò rỉ thông tin.</span>
          <select
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
            title="Số KH mỗi trang"
          >
            <option value={50}>50 KH/trang</option>
            <option value={100}>100 KH/trang</option>
            <option value={200}>200 KH/trang</option>
            <option value={500}>500 KH/trang</option>
          </select>
        </div>
        {/* MOBILE: danh sách dạng card (thiết kế). Bấm card = xem lịch sử/biểu đồ KH. */}
        <div className="only-mobile mlist">
          {loading ? <div className="empty">Đang tải…</div> : list.length ? list.map((c) => (
            <button key={c._id} type="button" className="mlist-card" onClick={() => openHistory(c)}>
              <span className="mlist-ava">{(c.name || c.code || '?').trim().slice(-1)[0] ? (c.name || '?').trim().split(/\s+/).slice(-1)[0][0].toUpperCase() : '?'}</span>
              <span className="mlist-main">
                <span className="mlist-title"><span className="mono" style={{ fontSize: 12, color: 'var(--text3)' }}>{c.code}</span> · {c.name}</span>
                <span className="mlist-sub">Sale: {c.sale || '—'}{c.ownerGroup ? ` · ${c.ownerGroup}` : ''}{c.managers?.length ? ` · QL: ${c.managers.map((m) => m.name).join(', ')}` : ''}</span>
              </span>
              <LevelBadge level={c.level} />
            </button>
          )) : <div className="empty">Chưa có khách hàng trong phạm vi của bạn</div>}
        </div>

        <div className="tbl-wrap framed only-desktop">
        <table>
          <thead>
            <tr>
              {canTask && <th style={{ width: 34, textAlign: 'center' }}><input type="checkbox" style={{ width: 'auto' }} checked={allShownPicked} onChange={toggleAll} title="Chọn tất cả trang này" /></th>}
              <th>Mã KH</th>
              <th>Tên KH</th>
              <th>Sale phụ trách</th>
              <th>Nhóm KD</th>
              <th>Người quản lý</th>
              <th>Phân cấp</th>
              <th>Nguồn KH</th>
              <th className="r">Cọc</th>
              <th className="r">Ngày tạo</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canTask ? 10 : 9} className="empty">
                  Đang tải...
                </td>
              </tr>
            ) : list.length ? (
              list.map((c) => (
                <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => openHistory(c)}>
                  {canTask && (
                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" style={{ width: 'auto' }} checked={picked.has(c._id)} onChange={() => togglePick(c._id)} />
                    </td>
                  )}
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {c.source === 'pmbox' ? (
                      // KH CHỈ có ở pmbox -> mã cam
                      <div style={{ color: 'var(--warn)' }} title="Mã KH bên PMBox">
                        {c.code}
                        {c.aliasCodes?.length > 0 && <div className="small" style={{ color: 'var(--warn)', opacity: 0.85 }} title="Mã chuyển (PMBox)">↳ {c.aliasCodes.join(', ')}</div>}
                      </div>
                    ) : (
                      <>
                        {/* SocSon: mã đen */}
                        <div style={{ color: 'var(--text)' }} title="Mã KH bên SocSon">
                          {c.code}
                          {c.aliasCodes?.length > 0 && <div className="small muted" title="Mã chuyển (SocSon)">↳ {c.aliasCodes.join(', ')}</div>}
                        </div>
                        {/* PMBox: mã cam (liên kết theo ID) */}
                        {c.pmboxCodes?.length > 0 && (
                          <div style={{ color: 'var(--warn)', marginTop: 3 }} title="Mã KH bên PMBox (liên kết cùng khách)">
                            {c.pmboxCodes.join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td
                    className="cust-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChart({ code: c.code, name: c.name });
                    }}
                    title="Xem biểu đồ doanh thu theo tháng"
                    style={{ fontWeight: 600 }}
                  >
                    {c.name}
                  </td>
                  <td>{c.sale || <span className="small muted">—</span>}</td>
                  <td>
                    {c.ownerGroup ? (
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'color-mix(in srgb, var(--accent) 13%, transparent)', padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                        {c.ownerGroup}
                      </span>
                    ) : (
                      <span className="small muted">—</span>
                    )}
                  </td>
                  <td>
                    {(c.managers && c.managers.length) ? (
                      <div style={{ lineHeight: 1.35 }}>
                        {c.managers.map((m, i) => (
                          <div key={i} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                            {m.name} <span className="small muted">· {m.role}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="small muted">—</span>
                    )}
                  </td>
                  <td><LevelBadge level={c.level} /></td>
                  <td>{c.sourceType || <span className="small muted">—</span>}</td>
                  <td className="r">
                    <RatioBadge r={c.ratio} />
                  </td>
                  <td className="r">{c.createdDate ? fmtDate(c.createdDate) : <span className="small muted">—</span>}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={canTask ? 10 : 9} className="empty">
                  Chưa có khách hàng trong phạm vi của bạn
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>

        {/* Thanh phân trang */}
        <div className="row between" style={{ marginTop: 14, flexWrap: 'wrap' }}>
          <span className="small muted">
            {total > 0
              ? `Hiển thị ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} / ${total} KH`
              : '0 KH'}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn ghost sm" onClick={() => setPage(1)} disabled={page <= 1 || loading}>
              « Đầu
            </button>
            <button className="btn ghost sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹ Trước
            </button>
            <span className="small" style={{ alignSelf: 'center', padding: '0 8px' }}>
              Trang <b>{page}</b> / {totalPages}
            </span>
            <button
              className="btn ghost sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Sau ›
            </button>
            <button className="btn ghost sm" onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading}>
              Cuối »
            </button>
          </div>
        </div>
      </div>

      <CustomerRevenueChartModal
        open={!!chart}
        code={chart?.code}
        name={chart?.name}
        onClose={() => setChart(null)}
      />

      {taskOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }} onClick={() => setTaskOpen(false)}>
          <div className="card" style={{ maxWidth: 480, width: '92%' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>📋 Tạo công việc từ {picked.size} khách hàng</h3>
            <p className="small muted" style={{ marginTop: 0 }}>
              Việc sẽ được gộp theo nhân viên phụ trách (mỗi NV 1 việc, kèm các KH của người đó).
              {me.role === 'TP' ? ' Giao thẳng xuống nhân viên trong nhóm bạn.' : ' Tạo xong → chờ trưởng phòng nhóm xác nhận rồi mới tới nhân viên.'}
            </p>
            <label className="small" style={{ display: 'block', marginTop: 10 }}>Tiêu đề *</label>
            <input value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} placeholder="VD: Gọi chăm sóc lại khách hàng" style={{ width: '100%', boxSizing: 'border-box' }} />
            <label className="small" style={{ display: 'block', marginTop: 10 }}>Nội dung chi tiết</label>
            <textarea value={taskForm.detail} onChange={(e) => setTaskForm((f) => ({ ...f, detail: e.target.value }))} rows={3} placeholder="Mô tả việc cần làm…" style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />
            <label className="small" style={{ display: 'block', marginTop: 10 }}>Hạn hoàn thành *</label>
            <input type="date" value={taskForm.deadline} onChange={(e) => setTaskForm((f) => ({ ...f, deadline: e.target.value }))} style={{ width: 'auto' }} />
            <div style={{ marginTop: 12 }}>
              <ResultStatusesField enabled={taskUseStatuses} onEnabled={setTaskUseStatuses} statuses={taskStatuses} onStatuses={setTaskStatuses} />
            </div>
            <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn ghost" onClick={() => setTaskOpen(false)}>Hủy</button>
              <button className="btn" disabled={taskBusy} onClick={submitTask}>{taskBusy ? 'Đang tạo…' : 'Tạo công việc'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Lấy nội dung + ngày từ 1 bản ghi có cấu trúc lỏng (khiếu nại / trao đổi / chuyển sale).
function pick(o, keys) {
  for (const k of keys) if (o && o[k] != null && o[k] !== '') return o[k];
  return '';
}
function itemContent(o) {
  if (typeof o === 'string') return o;
  return pick(o, ['content', 'message', 'note', 'text', 'title', 'description', 'action']) || JSON.stringify(o).slice(0, 100);
}
function itemDate(o) {
  return pick(o, ['created_date', 'createdAt', 'created_at', 'date']);
}

// Nhãn nguồn cho lịch sử trao đổi nội bộ.
const EXCHANGE_SRC = {
  note: { label: '💬 Ghi chú trực tiếp', color: 'var(--muted)', border: 'var(--border)' },
  task: { label: '📋 Công việc', color: 'var(--accent)', border: 'var(--accent)' },
  cskh: { label: '🎧 CSKH', color: 'var(--ok)', border: 'var(--ok)' },
};

// Lịch sử trao đổi nội bộ của 1 KH: gom note công việc + CSKH + ghi chú trực tiếp; cho gõ thêm.
function InternalExchange({ cust }) {
  const toast = useToast();
  const [hist, setHist] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => historyOfCustomer(cust._id).then(setHist).catch(() => setHist([]));
  useEffect(() => { load(); }, [cust._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function send() {
    const t = note.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await addCustomerNote(cust._id, t);
      setNote('');
      await load();
    } catch (err) {
      toast(err.response?.data?.message || 'Lỗi lưu ghi chú', 'danger');
    } finally {
      setBusy(false);
    }
  }

  const rows = hist || [];
  return (
    <>
      <div style={{ fontSize: 13, fontWeight: 700, margin: '4px 0 8px' }}>💬 Lịch sử trao đổi nội bộ ({rows.length})</div>
      <div className="card" style={{ background: 'var(--panel2)', marginBottom: 10 }}>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nhập nội dung trao đổi với khách hàng…" />
        <div className="row" style={{ marginTop: 6 }}>
          <div className="spacer" />
          <button className="btn sm" disabled={busy || !note.trim()} onClick={send}>{busy ? 'Đang gửi…' : '📤 Gửi ghi chú'}</button>
        </div>
      </div>
      {hist === null ? (
        <div className="empty">Đang tải…</div>
      ) : rows.length ? (
        rows.map((h) => {
          const s = EXCHANGE_SRC[h.source] || EXCHANGE_SRC.note;
          return (
            <div className="card" key={h._id} style={{ background: 'var(--panel2)', marginBottom: 8, borderLeft: `3px solid ${s.border}` }}>
              <div className="row between" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                  <b className="small">{h.by?.name || '—'}</b>
                  {h.by?.role && <span className="small muted">· {roleName(h.by.role)}</span>}
                  <span className="tag" style={{ color: s.color }}>{s.label}</span>
                </div>
                <span className="small muted">{fmtDateTime(h.at)}</span>
              </div>
              {h.source === 'task' && h.taskTitle && (
                <div className="small muted" style={{ margin: '2px 0 4px' }}>Công việc: {h.taskTitle}</div>
              )}
              {h.source === 'cskh' && <div className="small muted" style={{ margin: '2px 0 4px' }}>Trao đổi CSKH</div>}
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{h.note}</div>
            </div>
          );
        })
      ) : (
        <div className="empty">Chưa có trao đổi nào với KH này</div>
      )}
    </>
  );
}

// SocSon trả danh sách dạng { results:[...], pagination } -> bóc về mảng.
const asArr = (x) => (Array.isArray(x) ? x : x && Array.isArray(x.results) ? x.results : []);

// Format 1 nhân sự SocSon: "S000129 - Yến Sale".
function fmtStaff(s) {
  if (!s) return '—';
  const code = s.staff_code || '';
  const name = s.full_name || s.name || '';
  return [code, name].filter(Boolean).join(' - ') || '—';
}

// Lịch sử CHUYỂN SALE: title + sale hiện tại/cũ + người nhập + ngày.
function SaleHistoryList({ rows }) {
  const list = asArr(rows);
  if (!list.length) return <div className="empty" style={{ padding: 14 }}>Chưa có dữ liệu</div>;
  return (
    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
      {list.slice(0, 100).map((r, i) => (
        <div key={i} className="card" style={{ background: 'var(--panel2)', marginBottom: 6, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{r.title || 'Chuyển khách'}</div>
          {(r.staff_to || r.staff_from) && (
            <div className="small" style={{ marginTop: 3 }}>
              Sale hiện tại: <b>{fmtStaff(r.staff_to)}</b> · Sale cũ: {fmtStaff(r.staff_from)}
            </div>
          )}
          <div className="small muted" style={{ marginTop: 3 }}>
            C.Nhập: {fmtStaff(r.staff_update)}{itemDate(r) ? ` · ${fmtDateTime(itemDate(r))}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniList({ rows }) {
  const list = asArr(rows);
  if (!list.length) return <div className="empty" style={{ padding: 14 }}>Chưa có dữ liệu</div>;
  return (
    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
      {list.slice(0, 100).map((r, i) => (
        <div key={i} className="card" style={{ background: 'var(--panel2)', marginBottom: 6, padding: 10 }}>
          <div className="row between" style={{ gap: 8 }}>
            <span style={{ fontSize: 13 }}>{itemContent(r)}</span>
            {itemDate(r) ? <span className="small muted" style={{ whiteSpace: 'nowrap' }}>{fmtDate(itemDate(r))}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

// 4 mục chi tiết KH lấy từ SocSon.
function SocsonDetail({ detail }) {
  if (!detail) {
    return (
      <div className="card" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
        <div className="small muted">
          Chưa có chi tiết SocSon cho KH này. Hãy chạy "Chi tiết KH" trên extension để lấy doanh thu theo tháng,
          khiếu nại, lịch sử trao đổi & chuyển sale.
        </div>
      </div>
    );
  }

  // Doanh thu LŨY KẾ 12 tháng gần nhất — backend trả sẵn mảng {name:'T7.2026', total} (cũ -> mới).
  let revEntries = [];
  const rev = detail.revenue;
  if (Array.isArray(rev)) {
    revEntries = rev.map((r) => [pick(r, ['name', 'month', 'label']) || '—', Number(pick(r, ['total', 'amount', 'value'])) || 0]);
  } else if (rev && typeof rev === 'object') {
    revEntries = Object.entries(rev).filter(([, v]) => typeof v === 'number');
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>💲 Doanh thu lũy kế 12 tháng gần nhất</div>
      {revEntries.length ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', marginBottom: 12 }}>
          {revEntries.map(([k, v]) => (
            <div className="stat" key={k} style={{ padding: '8px 12px' }}>
              <div className="label">{k}</div>
              <div className="val sm">{fmtVND(v)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty" style={{ padding: 14, marginBottom: 12 }}>Chưa có doanh thu</div>
      )}

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>⚑ Quản lý khiếu nại ({asArr(detail.feedbacks).length})</div>
      <MiniList rows={detail.feedbacks} />

      <div style={{ fontSize: 13, fontWeight: 700, margin: '12px 0 8px' }}>💬 Lịch sử trao đổi - SocSon ({asArr(detail.conversations).length})</div>
      <MiniList rows={detail.conversations} />

      <div style={{ fontSize: 13, fontWeight: 700, margin: '12px 0 8px' }}>🔁 Lịch sử chuyển sale ({asArr(detail.histories).length})</div>
      <SaleHistoryList rows={detail.histories} />
    </div>
  );
}
